"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { fetchRecipesList, recipeKeys } from "@/lib/queries";
import { Skeleton } from "@/components/ui/Skeleton";

function youtubeThumb(url: string): string | null {
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([^&?/]+)/);
  return m ? `https://i.ytimg.com/vi/${m[1]}/mqdefault.jpg` : null;
}

const HOME_RECIPE_POOL = 30;
const HOME_RECIPE_SHOW = 6;
const ROLL_INTERVAL_MS = 8000;  // β-8 N1: 8초마다 자동 롤링

export function HomeRecipesSection() {
  // β-9 — 성장기 키워드 레시피 풀. 키워드당 1개 (홈 다양성 우선) → 호박인절미·우베라떼·... 30개 키워드.
  // 8초마다 6개 wrap-around 롤링 시 매번 다른 메뉴 노출.
  const { data, isPending } = useQuery({
    queryKey: recipeKeys.list("view", null, HOME_RECIPE_POOL, "growing", 1),
    queryFn: () => fetchRecipesList("view", null, HOME_RECIPE_POOL, "growing", 1),
  });

  // β-8 N1: 8초마다 다음 6개로 자동 회전 (wrap-around)
  const [rollIdx, setRollIdx] = useState(0);
  useEffect(() => {
    if (!data || data.length <= HOME_RECIPE_SHOW) return;
    const t = setInterval(() => {
      setRollIdx((i) => (i + HOME_RECIPE_SHOW) % data.length);
    }, ROLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [data]);

  const items = useMemo(() => {
    if (!data || data.length === 0) return [];
    const out: typeof data = [];
    for (let i = 0; i < HOME_RECIPE_SHOW; i++) {
      out.push(data[(rollIdx + i) % data.length]);
    }
    return out;
  }, [data, rollIdx]);

  return (
    <section className="px-4 mt-6 min-h-[200px]">
      <div className="flex items-center justify-between mb-3">
        <h2 className="section-title mb-0">🍳 요즘 뜨는 레시피</h2>
        <Link href="/recipes" className="text-xs text-text-sub hover:text-text transition-colors">
          전체 보기 →
        </Link>
      </div>

      {isPending ? (
        <div className="flex gap-3 overflow-x-auto hide-scrollbar">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="shrink-0 w-40">
              <Skeleton className="w-40 h-24 rounded-xl mb-2" />
              <Skeleton className="h-3 w-32 mb-1" />
              <Skeleton className="h-3 w-20" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="card text-center py-6">
          <p className="text-xs text-text-sub">아직 등록된 레시피가 없어요.</p>
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto hide-scrollbar -mx-4 px-4 pb-1">
          {items.map((r) => {
            const thumb = youtubeThumb(r.youtube_url);
            return (
              <Link
                key={r.id}
                href={`/recipe/${r.id}?from=${encodeURIComponent("/")}`}
                className="shrink-0 w-40 group"
              >
                {thumb ? (
                  <img
                    src={thumb}
                    alt=""
                    className="w-40 h-24 rounded-xl object-cover bg-bg-secondary group-hover:opacity-90 transition-opacity"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-40 h-24 rounded-xl bg-bg-secondary flex items-center justify-center text-2xl">
                    🎬
                  </div>
                )}
                <p className="mt-1.5 text-xs font-semibold text-text line-clamp-2 leading-snug">
                  {r.title}
                </p>
                <p className="mt-1 text-2xs text-text-sub">
                  #{r.keyword}
                  {r.view_count && r.view_count > 0 && (
                    <span className="ml-1.5">· {(r.view_count / 10000).toFixed(1)}만</span>
                  )}
                </p>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
